import * as React from 'react'
import { useParams } from 'react-router-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useUser } from '@/modules/shared/context/UserContext'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import {
  useQueryServerCompanies,
  useQueryAllServers,
  ServerCompanyModel,
  ServerListModel,
} from '@/lib/api/server/serverHooks'
import CompanyDatabasesPanel from '@/modules/databases/components/CompanyDatabasesPanel'
import DuplicateDatabaseModal from '@/modules/databases/components/DuplicateDatabaseModal'
import AddDatabaseModal from '@/modules/databases/components/AddDatabaseModal'
import { ServerAccessModel } from '@/modules/shared/types/account'
import { AddServerCompanyModal } from '@/modules/addCompany/components/AddServerCompanyModal'
import CompanyUsersPanel from '@/modules/addCompany/components/CompanyUsersPanel'
import CompaniesTable from '@/modules/addCompany/components/CompaniesTable'
import CompanyBasicActionBar from '@/modules/addCompany/components/CompanyBasicActionBar'
import CompanySelectedActionBar from '@/modules/addCompany/components/CompanySelectedActionBar'
import ManualCompanyModal from '@/modules/addCompany/components/ManualCompanyModal'
import CompanyGroupPicker from '@/modules/addCompany/components/CompanyGroupPicker'
import { CompanyGroupModel } from '@/modules/addCompany/types/companyGroups'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'

interface ServerListViewProps {
  server: { id: number; name: string; address: string }
  tenantCompanyId: string
  onSelectCompany: (company: ServerCompanyModel) => void
}

const CompanyTableTabContent: React.FC<{
  tenantCompanyId: string
  serverId: number
  serverName: string
  selectedGroupFilter: CompanyGroupModel | null
  serverCompanies: ServerCompanyModel[]
  onSelectCompany: (company: ServerCompanyModel) => void
}> = ({
  tenantCompanyId,
  serverId,
  serverName,
  selectedGroupFilter,
  serverCompanies,
  onSelectCompany,
}) => {
  const { isSelectedAny, selectedGroup, resetAll } = useTable()
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false)
  const [isAddFromDbOpen, setIsAddFromDbOpen] = React.useState(false)

  const allowlist = React.useMemo(
    () => new Set<string>(serverCompanies.map((c) => String(c.id))),
    [serverCompanies],
  )

  const handleRowClick = (company: CompanyDetailsModel) => {
    const match = serverCompanies.find((sc) => String(sc.id) === String(company.id))
    if (match) onSelectCompany(match)
  }

  return (
    <>
      <div className="tw-relative tw-bg-content-primary">
        {isSelectedAny ? (
          <CompanySelectedActionBar
            companyId={tenantCompanyId}
            selectedGroup={selectedGroup}
            resetAll={resetAll}
          />
        ) : (
          <CompanyBasicActionBar
            companyId={tenantCompanyId}
            totalCount={serverCompanies.length}
            onOpenCreateModal={() => setIsCreateModalOpen(true)}
            onOpenAddFromDatabase={() => setIsAddFromDbOpen(true)}
          />
        )}
      </div>

      <div className="tw-z-10 tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <CompaniesTable
          companyId={tenantCompanyId}
          selectedGroupFilter={selectedGroupFilter}
          companyIdsAllowlist={allowlist}
          onRowClick={handleRowClick}
        />
      </div>

      <ManualCompanyModal
        isOpen={isCreateModalOpen}
        close={() => setIsCreateModalOpen(false)}
        companyId={tenantCompanyId}
      />
      <AddServerCompanyModal
        isOpen={isAddFromDbOpen}
        close={() => setIsAddFromDbOpen(false)}
        serverId={serverId}
        serverName={serverName}
        tenantCompanyId={tenantCompanyId}
        defaultUseExistingDatabase
      />
    </>
  )
}

const ServerListView: React.FC<ServerListViewProps> = ({ server, tenantCompanyId, onSelectCompany }) => {
  const { data: serverCompanies = [], isLoading } = useQueryServerCompanies(server.id)
  const [selectedGroupFilter, setSelectedGroupFilter] = React.useState<CompanyGroupModel | null>(null)

  if (isLoading) {
    return (
      <div className="tw-grid tw-flex-1 tw-place-items-center tw-py-16">
        <LoaderIcon className="tw-w-16 tw-animate-spin" />
      </div>
    )
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-p-4">
        <CompanyGroupPicker
          companyId={tenantCompanyId}
          onSelect={setSelectedGroupFilter}
          actualGroup={selectedGroupFilter}
        />
      </div>

      <div className="tw-flex-1 tw-overflow-y-auto">
        <TableProvider bulk={true}>
          <CompanyTableTabContent
            tenantCompanyId={tenantCompanyId}
            serverId={server.id}
            serverName={server.name || server.address}
            selectedGroupFilter={selectedGroupFilter}
            serverCompanies={serverCompanies}
            onSelectCompany={onSelectCompany}
          />
        </TableProvider>
      </div>
    </div>
  )
}

interface DatabaseDetailViewProps {
  company: ServerCompanyModel
  onBack: () => void
}

const DatabaseDetailView: React.FC<DatabaseDetailViewProps> = ({ company, onBack }) => {
  const { t } = useLocalize()
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = React.useState(false)
  const [isAddDatabaseModalOpen, setIsAddDatabaseModalOpen] = React.useState(false)
  const [activeSubTab, setActiveSubTab] = React.useState<'databases' | 'users'>('databases')

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-bg-white tw-px-4 tw-py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2 tw-py-1 tw-text-sm tw-text-gray-600 hover:tw-bg-gray-100"
        >
          <svg className="tw-h-4 tw-w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('server-databases.back-to-list')}
        </button>
        <span className="tw-text-gray-300">|</span>
        <span
          className={`tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full ${company.isActive ? 'tw-bg-green-500' : 'tw-bg-gray-300'}`}
        />
        <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
          {company.name || company.uniqueIdentifier}
        </span>
        {company.databaseName && (
          <span className="tw-text-xs tw-text-gray-400">— {company.databaseName}</span>
        )}
        {activeSubTab === 'databases' && (
          <div className="tw-ml-auto">
            <button
              type="button"
              onClick={() => setIsAddDatabaseModalOpen(true)}
              className="tw-rounded-lg tw-bg-primary tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white hover:tw-opacity-90"
            >
              {t('server-databases.add-database')}
            </button>
          </div>
        )}
      </div>

      <nav className="tw-flex tw-border-b tw-border-base-300 tw-bg-white tw-px-4">
        <button
          type="button"
          onClick={() => setActiveSubTab('databases')}
          className={`tw-border-b-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium ${
            activeSubTab === 'databases'
              ? 'tw-border-primary tw-text-primary'
              : 'tw-border-transparent tw-text-content-base hover:tw-text-primary'
          }`}
        >
          {t('server-databases.tab-databases')}
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('users')}
          className={`tw-border-b-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium ${
            activeSubTab === 'users'
              ? 'tw-border-primary tw-text-primary'
              : 'tw-border-transparent tw-text-content-base hover:tw-text-primary'
          }`}
        >
          {t('server-databases.tab-users')}
        </button>
      </nav>

      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        {activeSubTab === 'databases' ? (
          <TableProvider bulk={true}>
            <CompanyDatabasesPanel
              companyId={company.id}
              onDuplicate={() => setIsDuplicateModalOpen(true)}
              onAddDatabase={() => setIsAddDatabaseModalOpen(true)}
            />
          </TableProvider>
        ) : (
          <CompanyUsersPanel companyId={company.id} />
        )}
      </div>

      <DuplicateDatabaseModal
        isOpen={isDuplicateModalOpen}
        close={() => setIsDuplicateModalOpen(false)}
        selectedCompanyIds={[company.id]}
        companyId={company.id}
      />
      <AddDatabaseModal
        isOpen={isAddDatabaseModalOpen}
        close={() => setIsAddDatabaseModalOpen(false)}
        companyId={company.id}
      />
    </div>
  )
}

interface ServerViewProps {
  server: { id: number; name: string; address: string }
  tenantCompanyId: string
}

const ServerView: React.FC<ServerViewProps> = ({ server, tenantCompanyId }) => {
  const [selectedCompany, setSelectedCompany] = React.useState<ServerCompanyModel | null>(null)

  return selectedCompany ? (
    <DatabaseDetailView company={selectedCompany} onBack={() => setSelectedCompany(null)} />
  ) : (
    <ServerListView server={server} tenantCompanyId={tenantCompanyId} onSelectCompany={setSelectedCompany} />
  )
}

function findServerForUser(
  serverId: number,
  serverAccess: ServerAccessModel[],
  allServers: ServerListModel[] | undefined,
): { id: number; name: string; address: string } | null {
  const fromAccess = serverAccess.find((s) => s.id === serverId)
  if (fromAccess) return { id: fromAccess.id, name: fromAccess.name, address: fromAccess.address }
  const fromAll = allServers?.find((s) => s.id === serverId)
  if (fromAll) return { id: fromAll.id, name: fromAll.name, address: fromAll.address }
  return null
}

export const ServerDatabasesPage: React.FC = () => {
  const { t } = useLocalize()
  const { isAdmin, getServerAccess } = useUser()
  const userIsAdmin = isAdmin()
  const params = useParams<{ companyId: string; serverId: string }>()
  const companyId = params.companyId
  const serverId = Number(params.serverId)

  const serverAccess = getServerAccess()
  const { data: allServers } = useQueryAllServers(userIsAdmin)

  const server = React.useMemo(
    () => findServerForUser(serverId, serverAccess, allServers),
    [serverId, serverAccess, allServers],
  )

  if (!Number.isFinite(serverId) || !companyId) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
        <p className="tw-text-sm tw-text-gray-500">{t('server-databases.no-access')}</p>
      </div>
    )
  }

  if (!server) {
    if (userIsAdmin && allServers == null) {
      return (
        <div className="tw-grid tw-flex-1 tw-place-items-center tw-py-16">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      )
    }
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
        <p className="tw-text-sm tw-text-gray-500">{t('server-databases.no-access')}</p>
      </div>
    )
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <ServerView key={server.id} server={server} tenantCompanyId={companyId} />
    </div>
  )
}
